"use client";

import React, {
  memo,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Check,
  ChevronDown,
  ChevronRight,
  Clipboard,
  Clock3,
  Eye,
  EyeOff,
  FileText,
  Loader2,
  LockKeyhole,
  LogOut,
  Paperclip,
  Plus,
  Search,
  SendHorizontal,
  Settings2,
  ShieldCheck,
  Sparkles,
  UserRound,
  X,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const STORAGE_KEYS = {
  unconstrainedSearch: "nlp-router-ui.unconstrainedSearch",
  researchMode: "nlp-router-ui.researchMode",
  adaptiveResearch: "nlp-router-ui.adaptiveResearch",
  activeChatId: "nlp-router-ui.activeChatId",
  attachedDocuments: "nlp-router-ui.attachedDocuments",
};

const RESEARCH_MODES = ["fast", "normal", "deep"];

const MAX_RENDERED_MARKDOWN_CHARS = 60000;

function defaultApiBase() {
  return process.env.NEXT_PUBLIC_API_BASE_URL || "http://localhost:9000";
}

function browserDefaultApiBase() {
  if (typeof window === "undefined") return defaultApiBase();
  if (process.env.NEXT_PUBLIC_API_BASE_URL) return process.env.NEXT_PUBLIC_API_BASE_URL;
  if (window.location.port === "3000") return "http://localhost:9000";
  return window.location.origin;
}

function readStoredValue(key, fallback) {
  try {
    const value = window.localStorage.getItem(key);
    return value === null ? fallback : value;
  } catch {
    return fallback;
  }
}

function readStoredBoolean(key, fallback) {
  try {
    const value = window.localStorage.getItem(key);
    if (value === null) return fallback;
    return value === "true";
  } catch {
    return fallback;
  }
}

function cls(...names) {
  return names.filter(Boolean).join(" ");
}

function normalizeApiBase(value) {
  return String(value || "").trim().replace(/\/+$/, "");
}

function scopedStorageKey(baseKey, userId) {
  return `${baseKey}:${encodeURIComponent(String(userId || ""))}`;
}

function clearScopedWorkspaceStorage(userId) {
  if (typeof window === "undefined" || !userId) return;
  try {
    window.localStorage.removeItem(scopedStorageKey(STORAGE_KEYS.activeChatId, userId));
    window.localStorage.removeItem(scopedStorageKey(STORAGE_KEYS.attachedDocuments, userId));
  } catch {}
}

async function readJsonResponse(response) {
  try {
    return await response.json();
  } catch {
    return {};
  }
}

function safeString(value) {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function truncateText(value, limit) {
  const text = safeString(value);
  if (text.length <= limit) return { text, truncated: false };
  return {
    text: `${text.slice(0, limit)}\n\n… truncated in the browser for UI safety. Open the raw API response if you need the full payload.`,
    truncated: true,
  };
}

function safeUrlTransform(url) {
  const raw = String(url || "").trim();
  if (!raw) return "";
  if (raw.startsWith("#") || raw.startsWith("/")) return raw;

  try {
    const parsed = new URL(raw);
    if (["http:", "https:", "mailto:"].includes(parsed.protocol)) return raw;
  } catch {
    return "";
  }

  return "";
}

function getDisplayResult(payload) {
  if (!payload) return "";
  if (payload.chat?.result) return payload.chat.result;
  if (payload.result) return payload.result;
  if (payload.answer) return payload.answer;

  const research = payload.research_agent_result;
  const wrapped = research?.result || research;
  if (typeof wrapped?.result === "string") return wrapped.result;
  if (typeof wrapped?.answer === "string") return wrapped.answer;
  if (typeof wrapped?.reasoning === "string") return wrapped.reasoning;
  if (typeof wrapped?.summary === "string") return wrapped.summary;
  if (typeof wrapped?.output?.reasoning === "string") return wrapped.output.reasoning;
  if (typeof wrapped?.output?.result === "string") return wrapped.output.result;

  return "No displayable result was returned. Open developer details to inspect the raw payload.";
}

function findFinalAnswerInput(payload) {
  let node = payload;
  for (let depth = 0; depth < 6; depth += 1) {
    if (!node || typeof node !== "object" || Array.isArray(node)) return null;
    if (node.final_answer_input && typeof node.final_answer_input === "object") {
      return node.final_answer_input;
    }
    if (node.evidence_pack && typeof node.evidence_pack === "object") {
      return node.evidence_pack;
    }
    if (node.developer_payload && typeof node.developer_payload === "object") {
      return node.developer_payload;
    }
    if (node.research_agent_result && typeof node.research_agent_result === "object") {
      node = node.research_agent_result;
      continue;
    }
    if (node.result && typeof node.result === "object") {
      node = node.result;
      continue;
    }
    return null;
  }
  return null;
}

function getFinalAnswerInput(item) {
  const explicit = item?.final_answer_input || item?.chat?.final_answer_input;
  if (explicit && typeof explicit === "object") return explicit;

  // The router exposes developer_payload as the exact final_answer_input object.
  const direct = item?.developer_payload || item?.chat?.developer_payload;
  if (direct && typeof direct === "object") return direct;

  const raw = item?.raw_response || item?.chat?.raw_response || item;
  return findFinalAnswerInput(raw);
}

function findFetchedSources(payload) {
  let node = payload;
  for (let depth = 0; depth < 8; depth += 1) {
    if (!node || typeof node !== "object" || Array.isArray(node)) return null;
    if (node.fetched_sources_payload && typeof node.fetched_sources_payload === "object") {
      return node.fetched_sources_payload;
    }
    if (node.fetched_sources && typeof node.fetched_sources === "object") {
      return node.fetched_sources;
    }
    if (node.detail && typeof node.detail === "object"
        && node.detail.fetched_sources && typeof node.detail.fetched_sources === "object") {
      return node.detail.fetched_sources;
    }
    if (node.research_agent_result && typeof node.research_agent_result === "object") {
      node = node.research_agent_result;
      continue;
    }
    if (node.result && typeof node.result === "object") {
      node = node.result;
      continue;
    }
    return null;
  }
  return null;
}

function getFetchedSources(item) {
  const direct = item?.fetched_sources_payload || item?.chat?.fetched_sources_payload;
  if (direct && typeof direct === "object") return direct;
  const raw = item?.raw_response || item?.chat?.raw_response || item;
  return findFetchedSources(raw);
}

function getAdaptive(item) {
  const direct = item?.adaptive || item?.chat?.adaptive;
  if (direct && typeof direct === "object") return direct;
  // Walk supported wrappers to find the adaptive diagnostics block (§36).
  let node = item?.raw_response || item?.chat?.raw_response || item;
  for (let depth = 0; depth < 6; depth += 1) {
    if (!node || typeof node !== "object" || Array.isArray(node)) return null;
    if (node.adaptive && typeof node.adaptive === "object") return node.adaptive;
    if (node.research_agent_result && typeof node.research_agent_result === "object") {
      node = node.research_agent_result;
      continue;
    }
    if (node.result && typeof node.result === "object") {
      node = node.result;
      continue;
    }
    return null;
  }
  return null;
}

function getResolutionDetail(item) {
  const raw = item?.raw_response || item?.chat?.raw_response || item;
  const agent = raw?.research_agent_result;
  const detail = agent?.detail;
  if (detail && typeof detail === "object" && detail.status === "CONSTRAINED_RESOLUTION_REQUIRED") {
    return detail;
  }
  return null;
}

function getResolutionExpandOption(detail) {
  return (detail?.resolution_options || []).find((item) => item?.type === "EXPAND_CONSTRAINED_DOMAINS") || null;
}

function getResolutionCandidates(detail) {
  const option = getResolutionExpandOption(detail);
  return Array.isArray(option?.candidate_domains) ? option.candidate_domains : [];
}

function getResolutionCandidateDiscoveryAction(detail) {
  const option = getResolutionExpandOption(detail);
  return option?.candidate_discovery_action?.action === "DISCOVER_CANDIDATE_DOMAINS"
    ? option.candidate_discovery_action
    : null;
}

function getResolutionCandidateDiscoverySummary(detail) {
  const summary = detail?.candidate_discovery_summary;
  return summary && typeof summary === "object" && !Array.isArray(summary) ? summary : null;
}

function getResolutionApprovalAction(detail) {
  const option = getResolutionExpandOption(detail);
  return option?.approval_action?.action === "APPROVE_DOMAINS"
    ? option.approval_action
    : null;
}

function getResolutionOpenWebAction(detail) {
  const option = (detail?.resolution_options || []).find((item) => item?.type === "OPEN_WEB");
  return option?.approval_action?.action === "OPEN_WEB" ? option.approval_action : null;
}

function getResolutionFinalizeAction(detail) {
  const option = (detail?.resolution_options || []).find((item) => item?.type === "FINALIZE_CURRENT_SCOPE");
  return option?.finalize_action?.action === "FINALIZE_CURRENT_SCOPE" ? option.finalize_action : null;
}

function getResolutionStatusLabel(detail) {
  const approvalAction = getResolutionApprovalAction(detail);
  const candidateDiscoveryAction = getResolutionCandidateDiscoveryAction(detail);
  const openWebAction = getResolutionOpenWebAction(detail);
  const finalizeAction = getResolutionFinalizeAction(detail);
  const actionCount = [approvalAction, candidateDiscoveryAction, finalizeAction, openWebAction].filter(Boolean).length;

  if (approvalAction && actionCount === 1) return "Approval required";
  if (openWebAction && actionCount === 1) return "Search authorization required";
  return "Resolution required";
}

function getDevPayloadLabel(item) {
  return getFinalAnswerInput(item)
    ? "final_answer_input.json"
    : "final_answer_input unavailable";
}

async function copyTextToClipboard(value) {
  const text = safeString(value);
  if (!text) return false;

  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return true;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  textarea.style.pointerEvents = "none";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  document.body.removeChild(textarea);
  return copied;
}

function formatDate(value) {
  if (!value) return "";
  try {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: "UTC",
    }).format(new Date(value));
  } catch {
    return value;
  }
}

function makeId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `chat-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function makeTitle(prompt) {
  const title = String(prompt || "").replace(/\s+/g, " ").trim();
  if (!title) return "New Query";
  return title.length > 60 ? `${title.slice(0, 60)}…` : title;
}

function makeEmptySession() {
  const now = new Date().toISOString();
  return {
    id: makeId(),
    title: "New Query",
    created_at: now,
    updated_at: now,
    messages: [],
    local_only: true,
  };
}

function normalizeSessions(value) {
  return dedupeSessions(value);
}


function dedupeMessages(messages) {
  if (!Array.isArray(messages)) return [];
  const seen = new Set();
  const result = [];

  for (const message of messages) {
    if (!message) continue;
    const stableKey = message.id
      ? `id:${message.id}`
      : `fallback:${message.chat_id || ""}:${message.created_at || ""}:${message.request_id || ""}:${safeString(message.prompt).slice(0, 120)}`;

    if (seen.has(stableKey)) continue;
    seen.add(stableKey);
    result.push(message);
  }

  return result;
}

function dedupeSessions(value) {
  if (!Array.isArray(value)) return [];
  const byId = new Map();

  for (const rawSession of value) {
    if (!rawSession || !rawSession.id) continue;
    const session = {
      ...rawSession,
      title: safeString(rawSession.title || "New Query"),
      messages: dedupeMessages(rawSession.messages),
    };

    const existing = byId.get(session.id);
    if (!existing) {
      byId.set(session.id, session);
      continue;
    }

    byId.set(session.id, {
      ...existing,
      ...session,
      created_at: existing.created_at || session.created_at,
      updated_at: session.updated_at || existing.updated_at,
      messages: dedupeMessages([...(existing.messages || []), ...(session.messages || [])]),
    });
  }

  return Array.from(byId.values());
}

function sortSessions(value) {
  return [...value].sort((a, b) => {
    const ad = new Date(a.updated_at || a.created_at || 0).getTime();
    const bd = new Date(b.updated_at || b.created_at || 0).getTime();
    return bd - ad;
  });
}

const CopyButton = memo(function CopyButton({ value, label, disabled = false }) {
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef(null);

  useEffect(() => () => {
    if (resetTimer.current) window.clearTimeout(resetTimer.current);
  }, []);

  const handleCopy = useCallback(async () => {
    if (disabled) return;
    try {
      const didCopy = await copyTextToClipboard(value);
      if (!didCopy) return;
      setCopied(true);
      if (resetTimer.current) window.clearTimeout(resetTimer.current);
      resetTimer.current = window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  }, [disabled, value]);

  const actionLabel = copied ? "Copied" : label;
  return (
    <button
      className={cls("copy-button", copied && "copied")}
      type="button"
      onClick={handleCopy}
      disabled={disabled}
      aria-label={actionLabel}
      title={actionLabel}
    >
      {copied ? <Check size={15} /> : <Clipboard size={15} />}
      <span className="sr-only" aria-live="polite">{actionLabel}</span>
    </button>
  );
});

const SafeMarkdown = memo(function SafeMarkdown({ children }) {
  const deferredSource = useDeferredValue(safeString(children));
  const { text, truncated } = useMemo(
    () => truncateText(deferredSource, MAX_RENDERED_MARKDOWN_CHARS),
    [deferredSource],
  );

  return (
    <>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        skipHtml
        urlTransform={safeUrlTransform}
        components={{
          img: () => null,
          a: ({ node, ...props }) => (
            <a {...props} target="_blank" rel="noopener noreferrer nofollow" />
          ),
        }}
      >
        {text}
      </ReactMarkdown>
      {truncated && <p className="muted">Large response truncated in the UI.</p>}
    </>
  );
});

const AdaptivePanel = memo(function AdaptivePanel({ adaptive }) {
  const [open, setOpen] = useState(false);
  if (!adaptive || typeof adaptive !== "object") return null;

  const plan = adaptive.research_plan || {};
  const policy = adaptive.request_policy || {};
  const resolution = adaptive.entity_resolution || {};
  const sufficiency = adaptive.evidence_sufficiency || {};
  const skills = adaptive.applied_skills || {};
  const coverage = adaptive.world_graph_coverage || {};
  const disclosures = Array.isArray(adaptive.disclosures) ? adaptive.disclosures : [];

  const selected = plan.selected_mode || policy.research_mode;
  const effective = plan.effective_mode;
  const modeChanged = selected && effective && selected !== effective;
  const resolutions = Array.isArray(resolution.resolutions) ? resolution.resolutions : [];
  const skillIds = Array.isArray(skills.selected_skill_ids) ? skills.selected_skill_ids : [];
  const covFraction = typeof coverage.coverage_fraction === "number"
    ? Math.round(coverage.coverage_fraction * 100) : null;
  const suffScore = typeof sufficiency.sufficiency_score === "number"
    ? Math.round(sufficiency.sufficiency_score * 100) : null;

  return (
    <div className="adaptive-panel">
      <button className="dev-toggle" type="button" onClick={() => setOpen((v) => !v)}>
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        Adaptive diagnostics
        {effective && (
          <span className={cls("mode-chip", `mode-${effective}`)}>
            {effective}{modeChanged ? ` (ceiling: ${selected})` : ""}
          </span>
        )}
      </button>

      {open && (
        <div className="adaptive-content">
          <div className="adaptive-grid">
            <div className="adaptive-stat">
              <span className="adaptive-label">Mode</span>
              <span>{selected || "—"} → <strong>{effective || "—"}</strong></span>
              {plan.research_shape && <span className="muted">{plan.research_shape}</span>}
            </div>
            {covFraction !== null && (
              <div className="adaptive-stat">
                <span className="adaptive-label">World-graph coverage</span>
                <span>{covFraction}%</span>
              </div>
            )}
            {suffScore !== null && (
              <div className="adaptive-stat">
                <span className="adaptive-label">Evidence sufficiency</span>
                <span>{suffScore}% · {sufficiency.recommended_action || "—"}</span>
              </div>
            )}
            {skillIds.length > 0 && (
              <div className="adaptive-stat">
                <span className="adaptive-label">Skills</span>
                <span>{skillIds.join(", ")}</span>
              </div>
            )}
          </div>

          {resolutions.length > 0 && (
            <div className="adaptive-block">
              <span className="adaptive-label">Entity resolution</span>
              <ul className="adaptive-list">
                {resolutions.map((r, i) => (
                  <li key={i}>
                    <strong>{r.mention || "—"}</strong>
                    {" → "}
                    {r.canonical_name || r.status}
                    <span className={cls("res-status", `res-${r.status}`)}>{r.status}</span>
                    {typeof r.confidence === "number" && (
                      <span className="muted"> {Math.round(r.confidence * 100)}%</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {resolution.comparison_scope && (
            <div className="adaptive-block">
              <span className="adaptive-label">Comparison scope</span>
              <span> {resolution.comparison_scope.strategy}
                {resolution.comparison_scope.temporal_alignment
                  ? ` · alignment: ${resolution.comparison_scope.temporal_alignment}` : ""}</span>
            </div>
          )}

          {disclosures.length > 0 && (
            <div className="adaptive-block">
              <span className="adaptive-label">Disclosures</span>
              <ul className="adaptive-list">
                {disclosures.map((d, i) => <li key={i}>{d}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

const DevDetails = memo(function DevDetails({ item }) {
  const [open, setOpen] = useState(false);
  const payload = useMemo(() => getFinalAnswerInput(item), [item]);
  const fetchedSources = useMemo(() => getFetchedSources(item), [item]);
  const payloadLabel = useMemo(() => getDevPayloadLabel(item), [item]);
  const rawJson = useMemo(() => {
    if (!payload) return "";
    try {
      return JSON.stringify(payload, null, 2);
    } catch {
      return "Could not serialize final_answer_input.";
    }
  }, [payload]);
  const fetchedSourcesJson = useMemo(() => {
    if (!fetchedSources) return "";
    try {
      return JSON.stringify(fetchedSources, null, 2);
    } catch {
      return "Could not serialize fetched_sources.";
    }
  }, [fetchedSources]);

  return (
    <div className="dev-panel">
      <div className="dev-header">
        <button className="dev-toggle" type="button" onClick={() => setOpen((value) => !value)}>
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          Dev Section
        </button>
        <CopyButton
          value={rawJson}
          label="Copy final_answer_input"
          disabled={!payload}
        />
        <CopyButton
          value={fetchedSourcesJson}
          label="Copy fetch_sources"
          disabled={!fetchedSources}
        />
      </div>

      {open && (
        <div className="dev-content">
          <details className="raw-json" open>
            <summary>{payloadLabel}</summary>
            {payload ? (
              <pre>{rawJson}</pre>
            ) : (
              <p className="muted">No final_answer_input was returned for this response.</p>
            )}
          </details>
          <details className="raw-json" open>
            <summary>{fetchedSources ? "fetch_sources.json (canonical fetched_sources.json)" : "fetch_sources.json unavailable"}</summary>
            {fetchedSources ? (
              <pre>{fetchedSourcesJson}</pre>
            ) : (
              <p className="muted">No fetch_sources payload was returned for this run.</p>
            )}
          </details>
        </div>
      )}
    </div>
  );
});

const ResolutionPanel = memo(function ResolutionPanel({ item, detail, onResolve, resolving = false }) {
  const candidates = useMemo(() => getResolutionCandidates(detail), [detail]);
  const candidateDiscoveryAction = useMemo(() => getResolutionCandidateDiscoveryAction(detail), [detail]);
  const candidateDiscoverySummary = useMemo(() => getResolutionCandidateDiscoverySummary(detail), [detail]);
  const approvalAction = useMemo(() => getResolutionApprovalAction(detail), [detail]);
  const openWebAction = useMemo(() => getResolutionOpenWebAction(detail), [detail]);
  const finalizeAction = useMemo(() => getResolutionFinalizeAction(detail), [detail]);
  const discoveryAttempted = Boolean(candidateDiscoverySummary?.attempted);
  const discoveredDomains = Array.isArray(candidateDiscoverySummary?.discovered_candidate_domains)
    ? candidateDiscoverySummary.discovered_candidate_domains.filter(Boolean)
    : [];
  const providerReturnedDomains = Array.isArray(candidateDiscoverySummary?.provider_returned_domains)
    ? candidateDiscoverySummary.provider_returned_domains.filter(Boolean)
    : discoveredDomains;
  const alreadyInScopeReturnedDomains = Array.isArray(candidateDiscoverySummary?.already_in_current_scope_returned_domains)
    ? candidateDiscoverySummary.already_in_current_scope_returned_domains.filter(Boolean)
    : [];
  const reusedDurableDomains = Array.isArray(candidateDiscoverySummary?.durably_approved_reused_domains)
    ? candidateDiscoverySummary.durably_approved_reused_domains.filter(Boolean)
    : [];
  const resolutionStatusLabel = getResolutionStatusLabel(detail);
  const openWebOnly = Boolean(openWebAction && !approvalAction && !candidateDiscoveryAction && !finalizeAction);
  const [selected, setSelected] = useState([]);
  useEffect(() => {
    setSelected((current) => current.filter((domain) => candidates.some((item) => item?.domain === domain)));
  }, [candidates]);

  const toggle = useCallback((domain) => {
    setSelected((current) => current.includes(domain)
      ? current.filter((value) => value !== domain)
      : [...current, domain]);
  }, []);

  return (
    <div className="resolution-panel">
      <div className="resolution-heading">
        <ShieldCheck size={17} />
        <strong>{resolutionStatusLabel}</strong>
      </div>
      <p>{safeString(detail?.message)}</p>
      {discoveryAttempted && (
        <div className="resolution-discovery-summary">
          {providerReturnedDomains.length > 0 ? (
            <>
              <p className="resolution-help">
                Candidate discovery returned {providerReturnedDomains.length} site{providerReturnedDomains.length === 1 ? "" : "s"}.
              </p>
              <p className="resolution-help">
                <strong>Returned sites:</strong> {providerReturnedDomains.join(", ")}
              </p>
              {alreadyInScopeReturnedDomains.length > 0 && (
                <p className="resolution-help">
                  Already in the current constrained scope: {alreadyInScopeReturnedDomains.join(", ")}
                </p>
              )}
              {discoveredDomains.length > 0 && (
                <p className="resolution-help">
                  <strong>Candidate-only sites:</strong> {discoveredDomains.join(", ")}
                </p>
              )}
              {reusedDurableDomains.length > 0 && (
                <p className="resolution-help">
                  Already approved and reused automatically before this pause: {reusedDurableDomains.join(", ")}
                </p>
              )}
              {candidates.length === 0 && reusedDurableDomains.length > 0 && (
                <p className="resolution-help">
                  No new candidate sites remain for approval. Their cached results were already reused and evaluated under your existing approvals.
                </p>
              )}
            </>
          ) : (
            <p className="resolution-help">Candidate discovery ran but found no additional candidate sites.</p>
          )}
        </div>
      )}
      {candidates.length > 0 && approvalAction ? (
        <>
          <p className="resolution-help">Select one or more websites to allow for this search.</p>
          <div className="resolution-domain-list">
            {candidates.map((candidate) => {
              const domain = safeString(candidate?.domain);
              const checked = selected.includes(domain);
              return (
                <label className="resolution-domain" key={domain}>
                  <input type="checkbox" checked={checked} onChange={() => toggle(domain)} disabled={resolving} />
                  <span>
                    <strong>{domain}</strong>
                    {candidate?.title && <small>{safeString(candidate.title)}</small>}
                  </span>
                </label>
              );
            })}
          </div>
          <button
            type="button"
            className="resolution-primary"
            disabled={resolving || selected.length === 0}
            onClick={() => onResolve?.(item, { ...approvalAction, domains: selected })}
          >
            {resolving ? <Loader2 className="spin" size={15} /> : <Check size={15} />}
            Use selected websites
          </button>
        </>
      ) : candidateDiscoveryAction ? (
        <button
          type="button"
          className="resolution-primary"
          disabled={resolving}
          onClick={() => onResolve?.(item, candidateDiscoveryAction)}
        >
          {resolving ? <Loader2 className="spin" size={15} /> : <Search size={15} />}
          Find candidate sites
        </button>
      ) : (
        <p className="resolution-help">No additional candidate-site discovery action is available for this paused state.</p>
      )}
      {openWebOnly && (
        <p className="resolution-help">
          No domain approval is pending. Open web is a separate authorization to search beyond the current constrained source set.
        </p>
      )}
      {finalizeAction && (
        <>
          <button
            type="button"
            className="resolution-secondary"
            disabled={resolving}
            onClick={() => onResolve?.(item, finalizeAction)}
          >
            Finish with current sources
          </button>
          <p className="resolution-help">
            Finish without approving a new site, opening the web, or performing another search. The run will return only what the current evidence supports.
          </p>
        </>
      )}
      {openWebAction && (
        <button
          type="button"
          className="resolution-secondary"
          disabled={resolving}
          onClick={() => onResolve?.(item, openWebAction)}
        >
          Use open web for this search
        </button>
      )}
      <p className="resolution-note">
        {discoveryAttempted
          ? "Candidate discovery has already run for this paused state. Existing-authority results shown above were already evaluated before this pause; only the actions shown above remain executable."
          : "Nothing is changed or searched until you choose an option."}
      </p>
    </div>
  );
});

const MessageCard = memo(function MessageCard({ item, pending = false, onResolve, resolving = false }) {
  const result = useMemo(() => item.result || getDisplayResult(item), [item]);
  const raw = item.raw_response || item.chat?.raw_response || item;
  const websearchRan = item.websearch_ran ?? raw?.websearch_ran;
  const researchAgentRan = (
    item.research_agent_ran
    ?? raw?.research_agent_ran
    ?? Boolean(websearchRan)
  );
  const resolution = getResolutionDetail(item);
  const failed = !resolution && (item.ok === false || raw?.ok === false);
  const statusClass = resolution ? "search" : failed ? "error" : researchAgentRan ? "search" : "skip";
  const statusLabel = resolution
    ? getResolutionStatusLabel(resolution)
    : failed
      ? "Request failed"
      : researchAgentRan
        ? "Research agent called"
        : "Router-only result";

  return (
    <article className={cls("message-card", pending && "pending")}>
      <div className="question-row">
        <div className="avatar user">U</div>
        <div>
          <p className="eyebrow">User query</p>
          <p className="question-text">{safeString(item.prompt)}</p>
        </div>
      </div>

      <div className="answer-row">
        <div className="avatar bot"><Sparkles size={16} /></div>
        <div className="answer-body">
          <div className="answer-meta">
            <span className={cls("status-pill", statusClass)}>
              {statusLabel}
            </span>
            {item.created_at && <span><Clock3 size={13} /> {formatDate(item.created_at)}</span>}
            {item.request_id && <span>Request {String(item.request_id).slice(0, 8)}</span>}
            {!pending && (
              <CopyButton value={safeString(result)} label="Copy answer" disabled={!result} />
            )}
          </div>

          {pending ? (
            <div className="loading-line">
              <Loader2 className="spin" size={17} /> Calling router and research agent...
            </div>
          ) : (
            <div className="result-text markdown-body">
              <SafeMarkdown>{result}</SafeMarkdown>
            </div>
          )}

          {!pending && resolution && (
            <ResolutionPanel item={item} detail={resolution} onResolve={onResolve} resolving={resolving} />
          )}
          {!pending && <AdaptivePanel adaptive={getAdaptive(item)} />}
          {!pending && <DevDetails item={item} />}
        </div>
      </div>
    </article>
  );
});

const LoginScreen = memo(function LoginScreen({
  userId,
  setUserId,
  password,
  setPassword,
  showPassword,
  setShowPassword,
  submitting,
  error,
  onSubmit,
}) {
  return (
    <main className="auth-shell">
      <section className="auth-visual" aria-hidden="true">
        <div className="auth-orb auth-orb-one" />
        <div className="auth-orb auth-orb-two" />
        <div className="auth-visual-content">
          <div className="auth-brand-mark"><Search size={28} /></div>
          <p className="eyebrow">EnigmaMind Search</p>
          <h1>Private research, grounded in evidence.</h1>
          <p>
            Sign in to access your own chat history, uploaded documents, and research workspace.
          </p>
          <div className="auth-security-note">
            <ShieldCheck size={20} />
            <span>Your password is verified by the API and is never stored in this browser.</span>
          </div>
        </div>
      </section>

      <section className="auth-panel">
        <form className="auth-card" onSubmit={onSubmit}>
          <div className="auth-card-heading">
            <div className="auth-lock"><LockKeyhole size={22} /></div>
            <div>
              <p className="eyebrow">Secure access</p>
              <h2>Sign in</h2>
              <p>Enter the user ID and password provided by your administrator.</p>
            </div>
          </div>

          {error && <div className="auth-error" role="alert">{error}</div>}

          <label className="auth-field">
            <span>User ID</span>
            <div className="auth-input-wrap">
              <UserRound size={18} />
              <input
                value={userId}
                onChange={(event) => setUserId(event.target.value)}
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                maxLength={50}
                required
                disabled={submitting}
                placeholder="Enter your user ID"
              />
            </div>
          </label>

          <label className="auth-field">
            <span>Password</span>
            <div className="auth-input-wrap">
              <LockKeyhole size={18} />
              <input
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                maxLength={1024}
                required
                disabled={submitting}
                placeholder="Enter your password"
              />
              <button
                className="auth-password-toggle"
                type="button"
                onClick={() => setShowPassword((value) => !value)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                disabled={submitting}
              >
                {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </div>
          </label>

          <button
            className="auth-submit"
            type="submit"
            disabled={submitting || !userId.trim() || !password}
          >
            {submitting ? <Loader2 className="spin" size={18} /> : <LockKeyhole size={18} />}
            {submitting ? "Signing in..." : "Sign in"}
          </button>

        </form>
      </section>
    </main>
  );
});

export default function Page() {
  const [apiBase, setApiBase] = useState(defaultApiBase);
  const [authStatus, setAuthStatus] = useState("checking");
  const [currentUser, setCurrentUser] = useState(null);
  const [loginUserId, setLoginUserId] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [loginSubmitting, setLoginSubmitting] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [workspaceSubject, setWorkspaceSubject] = useState("");
  const [unconstrainedSearch, setUnconstrainedSearch] = useState(true);
  const [researchMode, setResearchMode] = useState("normal");
  const [adaptiveResearch, setAdaptiveResearch] = useState(true);
  const [hasHydrated, setHasHydrated] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [sessions, setSessions] = useState([]);
  const [activeChatId, setActiveChatId] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resolvingMessageId, setResolvingMessageId] = useState(null);
  const [pendingPrompt, setPendingPrompt] = useState("");
  const [pendingCreatedAt, setPendingCreatedAt] = useState("");
  const [error, setError] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  const [attachedDocs, setAttachedDocs] = useState({}); // chatKey -> [{id, filename, state, size, error}]
  const [uploadingDocs, setUploadingDocs] = useState([]); // [{key, filename, progress}]
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const pollTimersRef = useRef({});
  const scrollerRef = useRef(null);
  const textareaRef = useRef(null);
  const loadedOnceRef = useRef(false);

  useEffect(() => {
    setApiBase(browserDefaultApiBase());
    setUnconstrainedSearch(readStoredBoolean(STORAGE_KEYS.unconstrainedSearch, true));
    const storedMode = readStoredValue(STORAGE_KEYS.researchMode, "normal");
    setResearchMode(RESEARCH_MODES.includes(storedMode) ? storedMode : "normal");
    setAdaptiveResearch(readStoredBoolean(STORAGE_KEYS.adaptiveResearch, true));
    setHasHydrated(true);
  }, []);

  const normalizedApiBase = useMemo(() => normalizeApiBase(apiBase), [apiBase]);

  const clearWorkspace = useCallback(() => {
    Object.values(pollTimersRef.current).forEach((timer) => window.clearTimeout(timer));
    pollTimersRef.current = {};
    loadedOnceRef.current = false;
    setWorkspaceSubject("");
    setSessions([]);
    setActiveChatId(null);
    setAttachedDocs({});
    setUploadingDocs([]);
    setPrompt("");
    setPendingPrompt("");
    setPendingCreatedAt("");
    setSubmitting(false);
    setLoadingHistory(false);
    setShowSettings(false);
    setError("");
  }, []);

  const markSignedOut = useCallback((message = "") => {
    clearScopedWorkspaceStorage(currentUser?.user_id || workspaceSubject);
    clearWorkspace();
    setCurrentUser(null);
    setAuthStatus("anonymous");
    setLoginPassword("");
    setLoginError(message);
  }, [clearWorkspace, currentUser?.user_id, workspaceSubject]);

  const authenticatedFetch = useCallback(async (path, options = {}) => {
    if (!normalizedApiBase) throw new Error("API base URL is required.");
    const response = await fetch(`${normalizedApiBase}${path}`, {
      ...options,
      credentials: "include",
      cache: options.cache || "no-store",
    });
    if (response.status === 401) {
      markSignedOut("Your session has expired. Sign in again.");
      const authError = new Error("Authentication required.");
      authError.authenticationRequired = true;
      throw authError;
    }
    return response;
  }, [markSignedOut, normalizedApiBase]);

  useEffect(() => {
    if (!hasHydrated || !normalizedApiBase) return undefined;
    let cancelled = false;
    clearWorkspace();
    setCurrentUser(null);
    setAuthStatus("checking");
    setLoginError("");

    const checkSession = async () => {
      try {
        const response = await fetch(`${normalizedApiBase}/auth/me`, {
          credentials: "include",
          cache: "no-store",
        });
        const data = await readJsonResponse(response);
        if (cancelled) return;
        if (response.ok && data?.ok && data?.user?.user_id) {
          setCurrentUser(data.user);
          setLoginUserId(data.user.user_id);
          setAuthStatus("authenticated");
          return;
        }
        setAuthStatus("anonymous");
        if (response.status !== 401) {
          setLoginError(data?.error || `Authentication check failed with ${response.status}.`);
        }
      } catch {
        if (!cancelled) {
          setAuthStatus("anonymous");
          setLoginError("Could not connect to the authentication server.");
        }
      }
    };

    checkSession();
    return () => { cancelled = true; };
  }, [clearWorkspace, hasHydrated, normalizedApiBase]);

  useEffect(() => {
    const userId = currentUser?.user_id;
    if (!hasHydrated || authStatus !== "authenticated" || !userId) return;

    let documents = {};
    let storedChatId = null;
    try {
      const storedDocuments = window.localStorage.getItem(
        scopedStorageKey(STORAGE_KEYS.attachedDocuments, userId),
      );
      if (storedDocuments) {
        const parsed = JSON.parse(storedDocuments);
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) documents = parsed;
      }
      storedChatId = window.localStorage.getItem(
        scopedStorageKey(STORAGE_KEYS.activeChatId, userId),
      );
    } catch {}

    loadedOnceRef.current = false;
    setSessions([]);
    setAttachedDocs(documents);
    setActiveChatId(storedChatId || null);
    setWorkspaceSubject(userId);
  }, [authStatus, currentUser?.user_id, hasHydrated]);

  const handleLogin = useCallback(async (event) => {
    event?.preventDefault?.();
    if (!normalizedApiBase || !loginUserId.trim() || !loginPassword || loginSubmitting) return;
    setLoginSubmitting(true);
    setLoginError("");
    try {
      const response = await fetch(`${normalizedApiBase}/auth/login`, {
        method: "POST",
        credentials: "include",
        cache: "no-store",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: loginUserId.trim(), password: loginPassword }),
      });
      const data = await readJsonResponse(response);
      if (!response.ok || !data?.ok || !data?.user?.user_id) {
        throw new Error(data?.error || `Sign-in failed with ${response.status}.`);
      }
      setCurrentUser(data.user);
      setLoginUserId(data.user.user_id);
      setLoginPassword("");
      setAuthStatus("authenticated");
    } catch (requestError) {
      setLoginPassword("");
      setAuthStatus("anonymous");
      setLoginError(requestError?.message || "Sign-in failed.");
    } finally {
      setLoginSubmitting(false);
    }
  }, [loginPassword, loginSubmitting, loginUserId, normalizedApiBase]);

  const handleLogout = useCallback(async () => {
    try {
      if (normalizedApiBase) {
        await fetch(`${normalizedApiBase}/auth/logout`, {
          method: "POST",
          credentials: "include",
          cache: "no-store",
        });
      }
    } catch {
      // Local sign-out must still complete if the server is temporarily unreachable.
    } finally {
      markSignedOut("");
    }
  }, [markSignedOut, normalizedApiBase]);

  const sortedSessions = useMemo(() => sortSessions(dedupeSessions(sessions)), [sessions]);
  const activeSession = useMemo(
    () => sessions.find((session) => session.id === activeChatId) || null,
    [activeChatId, sessions],
  );
  const activeMessages = useMemo(() => dedupeMessages(activeSession?.messages || []), [activeSession]);


  useEffect(() => {
    if (!hasHydrated) return;
    try { window.localStorage.setItem(STORAGE_KEYS.unconstrainedSearch, String(unconstrainedSearch)); } catch {}
  }, [unconstrainedSearch, hasHydrated]);

  useEffect(() => {
    if (!hasHydrated) return;
    try { window.localStorage.setItem(STORAGE_KEYS.researchMode, researchMode); } catch {}
  }, [researchMode, hasHydrated]);

  useEffect(() => {
    if (!hasHydrated) return;
    try { window.localStorage.setItem(STORAGE_KEYS.adaptiveResearch, String(adaptiveResearch)); } catch {}
  }, [adaptiveResearch, hasHydrated]);

  useEffect(() => {
    if (!hasHydrated || !workspaceSubject) return;
    try {
      window.localStorage.setItem(
        scopedStorageKey(STORAGE_KEYS.attachedDocuments, workspaceSubject),
        JSON.stringify(attachedDocs),
      );
    } catch {}
  }, [attachedDocs, hasHydrated, workspaceSubject]);

  useEffect(() => {
    if (!hasHydrated || !workspaceSubject) return;
    const key = scopedStorageKey(STORAGE_KEYS.activeChatId, workspaceSubject);
    try {
      if (activeChatId) window.localStorage.setItem(key, activeChatId);
      else window.localStorage.removeItem(key);
    } catch {}
  }, [activeChatId, hasHydrated, workspaceSubject]);

  const createNewChat = useCallback(() => {
    const session = makeEmptySession();
    setSessions((items) => sortSessions(dedupeSessions([session, ...items])));
    setActiveChatId(session.id);
    setPrompt("");
    setError("");
    window.requestAnimationFrame(() => textareaRef.current?.focus());
  }, []);

  const loadHistory = useCallback(async () => {
    if (authStatus !== "authenticated" || !workspaceSubject) return;
    setLoadingHistory(true);
    setError("");
    try {
      const res = await authenticatedFetch("/chat/history?limit=100");
      const data = await readJsonResponse(res);
      if (!res.ok || !data.ok) throw new Error(data.error || `History request failed with ${res.status}`);

      let loaded = normalizeSessions(data.sessions);
      if (!loaded.length && Array.isArray(data.history)) {
        loaded = normalizeSessions(data.history.map((item) => ({
          id: item.chat_id || item.id || makeId(),
          title: makeTitle(item.prompt),
          created_at: item.created_at,
          updated_at: item.created_at,
          messages: [item],
        })));
      }

      setSessions((current) => {
        const localEmpty = current.filter((session) => session.local_only && session.messages.length === 0);
        return sortSessions(dedupeSessions([...localEmpty, ...loaded]));
      });
      setActiveChatId((current) => current || loaded[0]?.id || null);
    } catch (historyError) {
      if (!historyError?.authenticationRequired) {
        setError(historyError?.message || "Could not load chat history.");
      }
    } finally {
      setLoadingHistory(false);
    }
  }, [authStatus, authenticatedFetch, workspaceSubject]);

  useEffect(() => {
    if (
      !hasHydrated
      || authStatus !== "authenticated"
      || !workspaceSubject
      || loadedOnceRef.current
    ) return;
    loadedOnceRef.current = true;
    loadHistory();
  }, [authStatus, hasHydrated, loadHistory, workspaceSubject]);

  useEffect(() => {
    const node = scrollerRef.current;
    if (!node) return;
    window.requestAnimationFrame(() => {
      node.scrollTo({ top: node.scrollHeight, behavior: "smooth" });
    });
  }, [activeChatId, activeMessages.length, submitting]);

  const chatDocsKey = activeChatId || "draft";
  const activeDocs = attachedDocs[chatDocsKey] || [];

  const updateDoc = useCallback((key, docId, patch) => {
    setAttachedDocs((all) => ({
      ...all,
      [key]: (all[key] || []).map((doc) => (doc.id === docId ? { ...doc, ...patch } : doc)),
    }));
  }, []);

  const pollDocumentStatus = useCallback((key, docId) => {
    if (pollTimersRef.current[docId]) return;
    const tick = async () => {
      try {
        const res = await authenticatedFetch(`/documents/${encodeURIComponent(docId)}`);
        const data = await readJsonResponse(res);
        if (res.status === 404) {
          updateDoc(key, docId, {
            state: "failed",
            error: "Document is no longer available.",
          });
          delete pollTimersRef.current[docId];
          return;
        }
        if (!res.ok) throw new Error(data?.error?.message || data?.error || `Document request failed with ${res.status}`);
        const doc = data?.data?.document || data?.document;
        if (doc?.state) {
          updateDoc(key, docId, { state: doc.state, pages: doc.page_count, chunks: doc.chunk_count, error: doc.error || "" });
          if (doc.state === "ready" || doc.state === "failed") {
            delete pollTimersRef.current[docId];
            return;
          }
        }
      } catch (pollError) {
        if (pollError?.authenticationRequired) {
          delete pollTimersRef.current[docId];
          return;
        }
      }
      pollTimersRef.current[docId] = window.setTimeout(tick, 2000);
    };
    pollTimersRef.current[docId] = window.setTimeout(tick, 1500);
  }, [authenticatedFetch, updateDoc]);

  useEffect(() => {
    // Resume polling only for documents in the authenticated user's workspace.
    if (!hasHydrated || authStatus !== "authenticated" || !workspaceSubject) return undefined;
    Object.entries(attachedDocs).forEach(([key, docs]) => {
      (docs || []).forEach((doc) => {
        if (doc.state === "pending" || doc.state === "processing") pollDocumentStatus(key, doc.id);
      });
    });
    return () => {
      Object.values(pollTimersRef.current).forEach((timer) => window.clearTimeout(timer));
      pollTimersRef.current = {};
    };
  }, [attachedDocs, authStatus, hasHydrated, pollDocumentStatus, workspaceSubject]);

  const uploadFiles = useCallback(async (fileList) => {
    if (authStatus !== "authenticated") {
      markSignedOut("Sign in to attach documents.");
      return;
    }
    const files = Array.from(fileList || []).filter((file) =>
      file && (file.type === "application/pdf" || /\.pdf$/i.test(file.name || "")));
    if (!files.length) {
      setError("Only PDF files can be attached.");
      return;
    }
    if (!normalizedApiBase) return;
    const key = chatDocsKey;

    for (const file of files) {
      const uploadKey = `${Date.now()}-${file.name}`;
      setUploadingDocs((items) => [...items, { key: uploadKey, filename: file.name, progress: 0 }]);
      try {
        const doc = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open("POST", `${normalizedApiBase}/documents/upload`);
          xhr.withCredentials = true;
          xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
              const progress = Math.round((event.loaded / event.total) * 100);
              setUploadingDocs((items) => items.map((item) => (item.key === uploadKey ? { ...item, progress } : item)));
            }
          };
          xhr.onload = () => {
            try {
              const data = JSON.parse(xhr.responseText || "{}");
              if (xhr.status === 401) {
                markSignedOut("Your session has expired. Sign in again.");
                const authError = new Error("Authentication required.");
                authError.authenticationRequired = true;
                reject(authError);
                return;
              }
              const record = data?.data?.document || data?.document;
              if (xhr.status >= 200 && xhr.status < 300 && record?.document_id) resolve(record);
              else reject(new Error(data?.error?.message || data?.error || `Upload failed with ${xhr.status}`));
            } catch (uploadError) { reject(uploadError); }
          };
          xhr.onerror = () => reject(new Error("Upload failed."));
          const form = new FormData();
          form.append("file", file, file.name);
          xhr.send(form);
        });

        setAttachedDocs((all) => {
          const existing = all[key] || [];
          if (existing.some((item) => item.id === doc.document_id)) return all;
          return {
            ...all,
            [key]: [...existing, {
              id: doc.document_id,
              filename: doc.filename || file.name,
              size: doc.size || file.size,
              state: doc.state || "pending",
              pages: doc.page_count,
              error: "",
            }],
          };
        });
        if (doc.state !== "ready") pollDocumentStatus(key, doc.document_id);
      } catch (uploadError) {
        if (!uploadError?.authenticationRequired) {
          setError(uploadError?.message || "Upload failed.");
        }
      } finally {
        setUploadingDocs((items) => items.filter((item) => item.key !== uploadKey));
      }
    }
  }, [authStatus, chatDocsKey, markSignedOut, normalizedApiBase, pollDocumentStatus]);

  const removeDocument = useCallback(async (docId) => {
    const key = chatDocsKey;
    setAttachedDocs((all) => ({ ...all, [key]: (all[key] || []).filter((doc) => doc.id !== docId) }));
    if (pollTimersRef.current[docId]) {
      window.clearTimeout(pollTimersRef.current[docId]);
      delete pollTimersRef.current[docId];
    }
    try {
      const response = await authenticatedFetch(`/documents/${encodeURIComponent(docId)}`, {
        method: "DELETE",
      });
      if (!response.ok && response.status !== 404) {
        const data = await readJsonResponse(response);
        throw new Error(data?.error?.message || data?.error || `Delete failed with ${response.status}`);
      }
    } catch (deleteError) {
      if (!deleteError?.authenticationRequired) {
        setError(deleteError?.message || "Could not delete the document.");
      }
    }
  }, [authenticatedFetch, chatDocsKey]);

  const resizeTextarea = useCallback((element) => {
    if (!element) return;
    element.style.height = "32px";
    element.style.height = `${Math.min(element.scrollHeight, 180)}px`;
  }, []);

  const resolveMessage = useCallback(async (item, resolution) => {
    if (!item?.id || resolvingMessageId || !normalizedApiBase) return;
    setResolvingMessageId(item.id);
    setError("");
    try {
      const response = await authenticatedFetch(`/chat/${encodeURIComponent(item.id)}/resolve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(resolution),
      });
      const data = await readJsonResponse(response);
      if (!data?.chat) {
        throw new Error(data?.error || `Resolution failed with ${response.status}`);
      }
      setSessions((items) => {
        const serverSession = data.session;
        const chatId = serverSession?.id || item.chat_id || activeChatId;
        const existing = items.find((session) => session.id === chatId);
        const base = serverSession || existing;
        if (!base) return items;
        const messages = serverSession?.messages
          || (existing?.messages || []).map((message) => message.id === data.chat.id ? data.chat : message);
        const next = { ...base, messages: dedupeMessages(messages), updated_at: serverSession?.updated_at || data.chat.updated_at || new Date().toISOString() };
        return sortSessions(dedupeSessions([next, ...items.filter((session) => session.id !== chatId)]));
      });
      if (data.session?.id) setActiveChatId(data.session.id);
      if (!response.ok && response.status !== 422) {
        setError(data?.error || data.chat?.result || `Resolution failed with ${response.status}`);
      }
    } catch (requestError) {
      if (!requestError?.authenticationRequired) setError(requestError?.message || "Resolution failed.");
    } finally {
      setResolvingMessageId(null);
    }
  }, [activeChatId, authenticatedFetch, normalizedApiBase, resolvingMessageId]);

  const submitPrompt = useCallback(async (event) => {
    event?.preventDefault?.();
    const text = prompt;
    if (!text.trim() || submitting || !normalizedApiBase) return;
    if (uploadingDocs.length > 0) {
      setError("Finish attaching the document before sending the query.");
      return;
    }

    const pendingDocuments = activeDocs.filter((doc) => doc.state !== "ready");
    if (pendingDocuments.length > 0) {
      setError("An attached document is not ready. Remove it or attach it again before sending.");
      return;
    }

    let chatId = activeChatId;
    if (!chatId) {
      const session = makeEmptySession();
      chatId = session.id;
      setSessions((items) => sortSessions(dedupeSessions([session, ...items])));
      setActiveChatId(chatId);
      // Migrate documents attached before the chat existed.
      setAttachedDocs((all) => {
        if (!all.draft?.length) return all;
        const { draft, ...rest } = all;
        return { ...rest, [chatId]: [...(all[chatId] || []), ...draft] };
      });
    }

    const documentIds = ((attachedDocs[chatId] || attachedDocs[chatDocsKey] || [])
      .filter((doc) => doc.state === "ready")
      .map((doc) => doc.id));

    setPrompt("");
    if (textareaRef.current) textareaRef.current.style.height = "32px";
    const createdAt = new Date().toISOString();
    setPendingPrompt(text);
    setPendingCreatedAt(createdAt);
    setSubmitting(true);
    setError("");

    const optimistic = {
      id: `pending-${Date.now()}`,
      chat_id: chatId,
      prompt: text,
      created_at: createdAt,
      result: "",
      websearch_ran: null,
      research_agent_ran: true,
    };

    const updateSessionWithMessage = (message, serverSession = null) => {
      setSessions((items) => {
        const now = message.created_at || new Date().toISOString();
        const existing = items.find((session) => session.id === chatId);
        const base = serverSession || existing || {
          id: chatId,
          title: makeTitle(text),
          created_at: now,
          updated_at: now,
          messages: [],
        };
        const nextSession = {
          ...base,
          id: chatId,
          title: base.title && base.title !== "New Query" ? base.title : makeTitle(text),
          updated_at: serverSession?.updated_at || now,
          local_only: false,
          messages: dedupeMessages(serverSession?.messages || [...(existing?.messages || []), message]),
        };
        return sortSessions(dedupeSessions([nextSession, ...items.filter((session) => session.id !== chatId)]));
      });
    };

    try {
      const body = JSON.stringify({
        chat_id: chatId,
        title: activeSession?.title || makeTitle(text),
        prompt: text,
        unconstrained_search: unconstrainedSearch,
        research_mode: researchMode,
        adaptive_research: adaptiveResearch,
        document_ids: documentIds,
      });
      const res = await authenticatedFetch("/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
      });
      const data = await readJsonResponse(res);
      if (!data.chat) {
        const backendDetail =
          data?.chat?.raw_response?.research_agent_result?.detail;
        const backendMessage =
          typeof backendDetail === "string"
            ? backendDetail
            : backendDetail?.message || backendDetail?.detail;
        throw new Error(
          data.error
          || data.chat?.result
          || backendMessage
          || `Chat request failed with ${res.status}`,
        );
      }
      updateSessionWithMessage(data.chat, data.session);
      setActiveChatId(data.session?.id || data.chat.chat_id || chatId);
    } catch (requestError) {
      if (!requestError?.authenticationRequired) {
        const message = requestError?.message || "Request failed.";
        updateSessionWithMessage({
          ...optimistic,
          result: message,
          ok: false,
          websearch_ran: false,
          research_agent_ran: true,
        });
        setError(message);
      }
    } finally {
      setSubmitting(false);
      setPendingPrompt("");
      setPendingCreatedAt("");
    }
  }, [activeChatId, activeDocs, activeSession, adaptiveResearch, attachedDocs, authenticatedFetch, chatDocsKey, normalizedApiBase, prompt, researchMode, submitting, unconstrainedSearch, uploadingDocs.length]);

  if (!hasHydrated || authStatus === "checking") {
    return (
      <main className="auth-loading" aria-live="polite">
        <div className="auth-loading-mark"><Search size={28} /></div>
        <Loader2 className="spin" size={22} />
        <p>Checking your session...</p>
      </main>
    );
  }

  if (authStatus !== "authenticated" || !currentUser?.user_id) {
    return (
      <LoginScreen
        userId={loginUserId}
        setUserId={setLoginUserId}
        password={loginPassword}
        setPassword={setLoginPassword}
        showPassword={showLoginPassword}
        setShowPassword={setShowLoginPassword}
        submitting={loginSubmitting}
        error={loginError}
        onSubmit={handleLogin}
      />
    );
  }

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-mark"><Search size={21} /></div>
          <div><h1>EnigmaMind Search</h1></div>
        </div>

        <button className="new-chat" type="button" onClick={createNewChat}>
          <Plus size={16} /> New query
        </button>

        <div className="history-block">
          <div className="section-title">Chats</div>
          {loadingHistory && <p className="muted">Loading saved chats...</p>}
          {!loadingHistory && sortedSessions.length === 0 && <p className="muted">No saved chats yet.</p>}
          {sortedSessions.map((session) => (
            <button
              key={session.id}
              className={cls("history-item", session.id === activeChatId && "active")}
              type="button"
              onClick={() => {
                setActiveChatId(session.id);
                setPrompt("");
              }}
            >
              <span>{session.title || "New Query"}</span>
              <small>{formatDate(session.updated_at || session.created_at)}</small>
            </button>
          ))}
        </div>
      </aside>

      <main className="main-panel">
        <header className="topbar">
          <div>
            <p className="eyebrow">EnigmaMind Search</p>
            {/* <h2>{activeSession?.title || "New Query"}</h2>*/}
          </div>
          <div className="topbar-actions">
            <div className="auth-user" title={`Signed in as ${currentUser.user_id}`}>
              <UserRound size={17} />
              <span>
                <small>Signed in</small>
                <strong>{currentUser.user_id}</strong>
              </span>
            </div>
            <button className="icon-button" type="button" onClick={() => setShowSettings((value) => !value)} aria-label="Settings">
              <Settings2 size={18} />
            </button>
            <button className="logout-button" type="button" onClick={handleLogout}>
              <LogOut size={17} /> Sign out
            </button>
          </div>
        </header>

        {showSettings && (
          <section className="settings-card">
            <div className="settings-readonly">
              <span>API endpoint</span>
              <code>{normalizedApiBase}</code>
            </div>
            <button type="button" onClick={loadHistory}>Reload history</button>
          </section>
        )}

        {error && <div className="error-banner">{error}</div>}

        <section className="chat-window" ref={scrollerRef}>
          {activeMessages.map((item, index) => (
            <MessageCard
              key={item.id || `${item.chat_id || activeChatId || "message"}-${item.created_at || index}-${index}`}
              item={item}
              onResolve={resolveMessage}
              resolving={resolvingMessageId === item.id}
            />
          ))}
          {submitting && (
            <MessageCard
              item={{
                prompt: pendingPrompt,
                created_at: pendingCreatedAt,
                websearch_ran: null,
                research_agent_ran: true,
              }}
              pending
            />
          )}
          {!hasHydrated && activeMessages.length === 0 && (
            <div className="empty-state">
              <Sparkles size={28} />
              <h3>Loading interface...</h3>
            </div>
          )}
          {hasHydrated && !submitting && activeMessages.length === 0 && (
            <div className="empty-state">
              <Sparkles size={28} />
              <h3>Start a new research query</h3>
              <p>Use New query for a separate chat, or send a prompt here to begin this chat.</p>
            </div>
          )}
        </section>

        <form
          className={cls("composer", dragActive && "drag-active")}
          onSubmit={submitPrompt}
          onDragOver={(event) => { event.preventDefault(); setDragActive(true); }}
          onDragLeave={(event) => { event.preventDefault(); setDragActive(false); }}
          onDrop={(event) => {
            event.preventDefault();
            setDragActive(false);
            uploadFiles(event.dataTransfer?.files);
          }}
        >
          {(activeDocs.length > 0 || uploadingDocs.length > 0) && (
            <div className="doc-chips">
              {activeDocs.map((doc) => (
                <span key={doc.id} className={cls("doc-chip", `doc-${doc.state || "pending"}`)} title={doc.error || doc.filename}>
                  <FileText size={13} />
                  <span className="doc-chip-name">{doc.filename}</span>
                  <span className="doc-chip-state">
                    {doc.state === "ready" && `ready${doc.pages ? ` · ${doc.pages}p` : ""}`}
                    {(doc.state === "pending" || doc.state === "processing") && (
                      <><Loader2 className="spin" size={11} /> processing</>
                    )}
                    {doc.state === "failed" && "failed"}
                  </span>
                  <button type="button" className="doc-chip-remove" aria-label={`Remove ${doc.filename}`} onClick={() => removeDocument(doc.id)}>
                    <X size={12} />
                  </button>
                </span>
              ))}
              {uploadingDocs.map((item) => (
                <span key={item.key} className="doc-chip doc-uploading">
                  <Loader2 className="spin" size={13} />
                  <span className="doc-chip-name">{item.filename}</span>
                  <span className="doc-chip-state">{item.progress}%</span>
                </span>
              ))}
            </div>
          )}
          {dragActive && <div className="drop-hint">Drop PDF files to attach them to this chat</div>}
          <div className="composer-wrap">
            <div className="composer-box">
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,.pdf"
                multiple
                hidden
                onChange={(event) => {
                  uploadFiles(event.target.files);
                  event.target.value = "";
                }}
              />
              <textarea
                ref={textareaRef}
                value={prompt}
                placeholder="Message EnigmaMind Search"
                rows={1}
                disabled={submitting}
                spellCheck={false}
                onChange={(event) => {
                  setPrompt(event.target.value);
                  resizeTextarea(event.target);
                }}
                onPaste={(event) => window.requestAnimationFrame(() => resizeTextarea(event.currentTarget))}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
                    event.preventDefault();
                    submitPrompt(event);
                  }
                }}
              />

              <div className="composer-actions">
                <button
                  type="button"
                  className="attach-button"
                  aria-label="Attach PDF documents"
                  title="Attach PDF documents"
                  disabled={submitting}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Paperclip size={17} />
                </button>

                <div className={cls("search-mode-select", unconstrainedSearch && "active")}>
                  <span className="toggle-dot" aria-hidden="true" />
                  <select
                    aria-label="Search constraint mode"
                    value={unconstrainedSearch ? "unconstrained" : "constrained"}
                    onChange={(event) => setUnconstrainedSearch(event.target.value === "unconstrained")}
                    title={unconstrainedSearch
                      ? "Open-web search (unconstrained_search=true)"
                      : "Use the backend allowlist from DOMAIN_PROFILES_PATH (unconstrained_search=false)"}
                  >
                    <option value="unconstrained">Unconstrained</option>
                    <option value="constrained">Constrained</option>
                  </select>
                  <ChevronDown size={14} aria-hidden="true" />
                </div>

                <div className="search-mode-select" title="Selected effort ceiling; the agent may run below it when adaptive.">
                  <select
                    aria-label="Research mode"
                    value={researchMode}
                    onChange={(event) => setResearchMode(event.target.value)}
                  >
                    <option value="fast">Fast</option>
                    <option value="normal">Normal</option>
                    <option value="deep">Deep</option>
                  </select>
                  <ChevronDown size={14} aria-hidden="true" />
                </div>

                <div className={cls("search-mode-select", adaptiveResearch && "active")}>
                  <span className="toggle-dot" aria-hidden="true" />
                  <select
                    aria-label="Adaptive research"
                    value={adaptiveResearch ? "adaptive" : "fixed"}
                    onChange={(event) => setAdaptiveResearch(event.target.value === "adaptive")}
                    title={adaptiveResearch ? "Controller may lower effort below the selected mode" : "Effort is fixed at the selected mode"}
                  >
                    <option value="adaptive">Adaptive</option>
                    <option value="fixed">Fixed</option>
                  </select>
                  <ChevronDown size={14} aria-hidden="true" />
                </div>

                <button
                  className="send-button"
                  type="submit"
                  disabled={!prompt.trim() || submitting || uploadingDocs.length > 0 || activeDocs.some((doc) => doc.state !== "ready")}
                  aria-label="Send message"
                  title={uploadingDocs.length > 0 || activeDocs.some((doc) => doc.state !== "ready") ? "Finish attaching the document before sending" : "Send"}
                >
                  {submitting ? <Loader2 className="spin" size={18} /> : <SendHorizontal size={18} />}
                </button>
              </div>
            </div>
          </div>
        </form>
      </main>
    </div>
  );
}
